/**
 * Cross-Domain Cart Sync + Age-State-Gate Reconciliation
 *
 * Keeps cart session, customer greeting, and age/shipping-state verification
 * aligned across the Hall brand domains. A single cross-domain transport —
 * the `?cart_token=…` URL param — links the visitor's cart on arrival; from
 * that point on, `cart.attributes` (`age_verified`, `shipping_state`,
 * `age_gate_flow`, `member_since`) propagate everywhere the cart goes.
 *
 * Local cookies (`age_verified`, `shipping_state_mf`) are a FOUC cache only.
 * They are written from cart.attributes after `/cart.js` resolves on each
 * domain, and read synchronously by the inline `<head>` snippet in
 * `layout/theme.liquid` to set `html.age-state-gate-verified` before first paint.
 * Modal visibility is fail-open: the gate is hidden by default and shown only
 * when section-age-state-gate.js determines verification is still needed.
 *
 * Requires `window.__cartSyncDomains` (from `shop.metafields.hallwines.cart_sync`)
 * for inbound cart-token claim and outbound cross-domain link patching. The
 * cart→cookie reconciliation and greeting populate run regardless.
 *
 * Public API consumed by `section-age-state-gate.js`:
 *   window.__isAgeVerifiedCookie()
 *   window.__getShippingStateFromCookie()
 *   window.__writeAgeVerifiedCookie()           — cookie only; no html class flip
 *   window.__markAgeVerified()                  — cookie + html class
 *   window.__markShippingState(code)
 *   window.__clearShippingStateCookie()
 *   window.__isStateSkippedCookie()
 *   window.__markStateSkipped()
 *   window.__clearStateSkippedCookie()
 *   window.__hallwinesCrossDomainPatchLinks()
 *
 * Events dispatched on `document`:
 *   hallwines:shipping-state:change  — { detail: { code: 'CA' | '' } }
 *     Fires whenever the shipping state cookie actually changes value (or is
 *     cleared). Use for section re-renders via the Shopify Section Rendering API.
 */
(function () {
  'use strict';

  var DOMAINS = window.__cartSyncDomains;
  var hasWhitelist = Array.isArray(DOMAINS) && DOMAINS.length > 0;
  var CURRENT_HOST = window.location.hostname;
  var hostOk = hasWhitelist && DOMAINS.indexOf(CURRENT_HOST) !== -1;

  var CART_COOKIE = 'cart';
  var AGE_COOKIE = 'age_verified';
  var STATE_COOKIE = 'shipping_state_mf';
  var STATE_SKIPPED_COOKIE = 'shipping_state_mf_skipped';
  var CART_TOKEN_MAX_AGE = 60 * 60 * 24 * 30;
  var AGE_MAX_AGE = 60 * 60 * 24 * 365;
  var SESSION_SYNCED_KEY = 'age-verified-synced';
  var HTML_VERIFIED_CLASS = 'age-state-gate-verified';

  /** US state + DC codes (must match overlay `<select>` options). */
  var US_STATE_CODES = {};
  'AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY'
    .split(/\s+/)
    .forEach(function (c) {
      US_STATE_CODES[c] = 1;
    });

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
  }

  function secureFlag() {
    return window.location.protocol === 'https:' ? '; Secure' : '';
  }

  // Registrable domain (host minus a leading "www.") so www + apex share the
  // verification cookies. The cart cookie stays host-only to match Shopify's
  // own scoping. Host-only duplicates from earlier releases are expired on
  // write so they can't shadow the domain-scoped value.
  var COOKIE_DOMAIN = window.location.hostname.replace(/^www\./, '');

  function domainFlag() {
    return '; domain=' + COOKIE_DOMAIN;
  }

  function expireHostOnly(name) {
    document.cookie = name + '=; path=/; max-age=0';
  }

  function setCartCookie(value) {
    document.cookie = CART_COOKIE + '=' + value + '; path=/; max-age=' + CART_TOKEN_MAX_AGE;
  }

  function writeAgeCookie() {
    expireHostOnly(AGE_COOKIE);
    document.cookie =
      AGE_COOKIE + '=1; path=/; max-age=' + AGE_MAX_AGE + '; SameSite=Lax' + domainFlag() + secureFlag();
  }

  function normalizeStateCode(raw) {
    if (raw == null) return '';
    var s = String(raw).trim().toUpperCase();
    return US_STATE_CODES[s] ? s : '';
  }

  // Fires on every actual change to the shipping state (including clears). Listeners
  // can refresh dependent sections via the Shopify Section Rendering API.
  /** @param {string} code */
  function dispatchStateChange(code) {
    try {
      document.dispatchEvent(
        new CustomEvent('hallwines:shipping-state:change', {
          bubbles: true,
          detail: { code: code || '' }
        })
      );
    } catch (e) {}
  }

  function writeStateCookie(code) {
    var v = normalizeStateCode(code);
    if (!v) return;
    var prev = readStateCookie();
    expireHostOnly(STATE_COOKIE);
    document.cookie =
      STATE_COOKIE +
      '=' +
      encodeURIComponent(v) +
      '; path=/; max-age=' +
      AGE_MAX_AGE +
      '; SameSite=Lax' +
      domainFlag() +
      secureFlag();
    if (prev !== v) dispatchStateChange(v);
  }

  function deleteStateCookie() {
    var prev = readStateCookie();
    expireHostOnly(STATE_COOKIE);
    document.cookie = STATE_COOKIE + '=; path=/; max-age=0; SameSite=Lax' + domainFlag() + secureFlag();
    if (prev) dispatchStateChange('');
  }

  function writeStateSkippedCookie() {
    expireHostOnly(STATE_SKIPPED_COOKIE);
    document.cookie =
      STATE_SKIPPED_COOKIE +
      '=1; path=/; max-age=' +
      AGE_MAX_AGE +
      '; SameSite=Lax' +
      domainFlag() +
      secureFlag();
  }

  function readStateSkippedCookie() {
    return getCookie(STATE_SKIPPED_COOKIE) === '1';
  }

  function deleteStateSkippedCookie() {
    expireHostOnly(STATE_SKIPPED_COOKIE);
    document.cookie =
      STATE_SKIPPED_COOKIE + '=; path=/; max-age=0; SameSite=Lax' + domainFlag() + secureFlag();
  }

  function readStateCookie() {
    var raw = getCookie(STATE_COOKIE);
    if (raw == null || raw === '') return '';
    try {
      raw = decodeURIComponent(raw);
    } catch (e) {}
    return normalizeStateCode(raw);
  }

  function isAgeVerified() {
    return getCookie(AGE_COOKIE) === '1';
  }

  function markAgeVerifiedWithClass() {
    writeAgeCookie();
    document.documentElement.classList.add(HTML_VERIFIED_CLASS);
  }

  window.__isAgeVerifiedCookie = isAgeVerified;
  window.__getShippingStateFromCookie = readStateCookie;
  window.__writeAgeVerifiedCookie = writeAgeCookie;
  window.__markAgeVerified = markAgeVerifiedWithClass;
  window.__markShippingState = writeStateCookie;
  window.__clearShippingStateCookie = deleteStateCookie;
  /** @type {any} */ (window).__isStateSkippedCookie = readStateSkippedCookie;
  /** @type {any} */ (window).__markStateSkipped = writeStateSkippedCookie;
  /** @type {any} */ (window).__clearStateSkippedCookie = deleteStateSkippedCookie;

  /**
   * Claim a cart from an inbound `?cart_token=` URL param. Sets the cart
   * cookie synchronously, cleans the URL, then dispatches a `cart:update`
   * event and prompts a one-shot login so the account UI hydrates.
   */
  function handleInboundCartToken() {
    var params = new URLSearchParams(window.location.search);
    var incomingToken = params.get('cart_token');
    if (!incomingToken) return;

    setCartCookie(incomingToken);

    params.delete('cart_token');
    var qs = params.toString();
    history.replaceState(
      {},
      '',
      window.location.pathname + (qs ? '?' + qs : '') + window.location.hash
    );

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        // Drop auto-open for one tick so this background sync doesn't pop the
        // drawer (CartAddEvent shares the 'cart:update' name we're dispatching).
        var drawer = document.querySelector('cart-drawer-component[auto-open]');
        if (drawer) drawer.removeAttribute('auto-open');
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: 'cross-domain-cart-sync',
              data: { itemCount: cart.item_count, source: 'cross-domain-sync' }
            }
          })
        );
        if (drawer) {
          var drawerEl = drawer;
          setTimeout(function () {
            drawerEl.setAttribute('auto-open', '');
          }, 0);
        }
      })
      .catch(function () {});

    // The legacy account-sheet auto-prompt (pendingLoginPrompt and its
    // whenHeaderSettled / whenAccountMenuHydrated timing helpers) was removed:
    // the silent login handshake (?xd_login= / cross-domain-login snippet)
    // supersedes it. section-age-state-gate.js's trigger helper self-guards on
    // the absent __hallwinesTriggerPendingLoginPrompt export.
  }

  /**
   * Reconcile the cart's attributes with the local cookies + html class.
   * Runs once per session. Two directions:
   *
   *   cart → cookie  — cart says verified → refresh the cookie cache and
   *                    apply the html class. Cart wins when both agree.
   *
   *   cookie → cart  — cart is missing attributes but the cookie says the
   *                    visitor has already verified. Happens after checkout
   *                    (Shopify replaces the old cart with an empty one) or
   *                    any other time the cart gets reset while the local
   *                    verification cookie is still valid. Re-POST the
   *                    cookie values to /cart/update.js so the new cart
   *                    carries them forward, and apply the html class
   *                    locally.
   *
   * In design mode we still apply the html class locally but skip all cart
   * and cookie writes so the merchant's real browser session isn't polluted.
   */
  async function reconcileLocalFromCart() {
    try {
      if (sessionStorage.getItem(SESSION_SYNCED_KEY) === '1') return;
    } catch (e) {}

    var inDesignMode = false;
    try {
      inDesignMode = document.documentElement.classList.contains('shopify-design-mode');
    } catch (e) {}

    try {
      var res = await fetch('/cart.js', { credentials: 'same-origin' });
      var cart = await res.json();
      var attrs = (cart && cart.attributes) || {};

      var cartAge = String(attrs.age_verified || '') === '1';
      var cartState = normalizeStateCode(attrs.shipping_state);
      var cartStateSkipped = String(attrs.shipping_state_skipped || '') === '1';
      var cookieAge = isAgeVerified();
      var cookieState = readStateCookie();
      var cookieStateSkipped = readStateSkippedCookie();

      if (cartAge) {
        // cart → cookie: cart is authoritative, refresh the local cache.
        if (!inDesignMode) writeAgeCookie();
        document.documentElement.classList.add(HTML_VERIFIED_CLASS);
        if (cartState) {
          // Real state wins over any lingering skip flag.
          if (!inDesignMode) writeStateCookie(cartState);
          if (!inDesignMode && cookieStateSkipped) deleteStateSkippedCookie();
        } else {
          if (!inDesignMode && cookieState) deleteStateCookie();
          if (!inDesignMode && cartStateSkipped) writeStateSkippedCookie();
        }
      } else if (cookieAge) {
        // cookie → cart: new/empty cart after checkout, rehydrate from cookie.
        document.documentElement.classList.add(HTML_VERIFIED_CLASS);
        if (!inDesignMode) {
          /** @type {Record<string, string>} */
          var rehydrate = { age_verified: '1' };
          if (cookieState) {
            rehydrate.shipping_state = cookieState;
            rehydrate.age_gate_flow = 'full';
          } else if (cookieStateSkipped) {
            rehydrate.shipping_state_skipped = '1';
            rehydrate.age_gate_flow = 'skipped';
          } else {
            rehydrate.age_gate_flow = 'age_only';
          }
          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ attributes: rehydrate })
          }).catch(function () {});
        }
      }

      try {
        sessionStorage.setItem(SESSION_SYNCED_KEY, '1');
      } catch (e) {}
    } catch (e) {
      // Non-critical
    }
  }

  /**
   * Append `cart_token` to `<a>` hrefs pointing at other configured brand
   * domains so the visitor's cart (and everything on it) travels with them.
   *
   * When the visitor has completed the age gate, also append `age_verified=1`
   * and (if set) `shipping_state_mf=<XX>` so the destination brand's inline
   * FOUC script can flip the verified class synchronously — no modal flash
   * on the first cross-domain page load. The cookies and cart attrs remain
   * the persistent sources of truth; URL params are a one-shot fast path.
   */
  function patchCrossDomainLinks() {
    var cartToken = getCookie(CART_COOKIE);
    var ageOk = isAgeVerified();
    // Signed-in hint (non-credential, server-rendered signals only): lets the
    // destination auto-initiate the silent login handshake (cross-domain-login).
    var greetingEl = document.getElementById('account-greeting');
    var loggedInHere =
      (greetingEl && greetingEl.getAttribute('data-customer-authenticated') === 'true') ||
      !!document.querySelector('shopify-account[data-account-logout-url]');
    // Age/state params travel even when no cart exists yet — verification and
    // cart transport are independent signals.
    if (!cartToken && !ageOk && !loggedInHere) return;

    var selectors = [];
    for (var i = 0; i < DOMAINS.length; i++) {
      if (DOMAINS[i] !== CURRENT_HOST) {
        selectors.push('a[href*="' + DOMAINS[i] + '"]');
      }
    }
    if (!selectors.length) return;

    var stateCode = ageOk ? readStateCookie() : '';
    var stateSkipped = ageOk && !stateCode && readStateSkippedCookie();

    var links = document.querySelectorAll(selectors.join(', '));
    links.forEach(function (link) {
      var href = link.getAttribute('href');
      if (!href) return;
      try {
        var url = new URL(href, window.location.origin);
        if (url.hostname === CURRENT_HOST) return;
        if (DOMAINS.indexOf(url.hostname) === -1) return;

        var changed = false;
        if (cartToken && url.searchParams.get('cart_token') !== cartToken) {
          url.searchParams.set('cart_token', cartToken);
          changed = true;
        }
        if (ageOk && url.searchParams.get(AGE_COOKIE) !== '1') {
          url.searchParams.set(AGE_COOKIE, '1');
          changed = true;
        }
        if (stateCode && url.searchParams.get(STATE_COOKIE) !== stateCode) {
          url.searchParams.set(STATE_COOKIE, stateCode);
          changed = true;
        }
        if (stateSkipped && url.searchParams.get(STATE_SKIPPED_COOKIE) !== '1') {
          url.searchParams.set(STATE_SKIPPED_COOKIE, '1');
          changed = true;
        }
        if (loggedInHere && url.searchParams.get('xd_login') !== '1') {
          url.searchParams.set('xd_login', '1');
          changed = true;
        }
        if (changed) link.setAttribute('href', url.toString());
      } catch (e) {
        // Malformed URL — skip
      }
    });
  }

  window.__hallwinesCrossDomainPatchLinks = hostOk ? patchCrossDomainLinks : function () {};

  /**
   * Re-run the link patcher after dynamic DOM updates. Faceted collections,
   * search infinite scroll, the cart drawer, and predictive search all swap
   * in fresh `<a>` markup via the Section Rendering API after the initial
   * DOMContentLoaded patch — those links would otherwise miss the cart token.
   *
   * A single debounced MutationObserver covers every re-render path without
   * coupling to each feature's JS. `patchCrossDomainLinks` only writes a href
   * when the token is missing, so re-patching already-patched links is a
   * no-op and the observer settles after one pass.
   */
  function observeForRepatching() {
    if (!document.body || typeof MutationObserver === 'undefined') return;

    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      setTimeout(function () {
        scheduled = false;
        patchCrossDomainLinks();
      }, 250);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Trust only Liquid-rendered customer state for the header greeting.
   */
  function populateGreeting() {
    var greeting = document.getElementById('account-greeting');
    if (!greeting) return;

    sessionStorage.removeItem('customer_name');
    sessionStorage.removeItem('customer_member_since');

    if (greeting.dataset.customerAuthenticated !== 'true') {
      greeting.textContent = '';
      return;
    }

    syncMemberSinceToCart();
  }

  function injectAccountLogoutLink(account) {
    if (!account || !account.dataset.accountLogoutUrl || !account.shadowRoot) return false;

    var shortcuts = account.shadowRoot.querySelector('.account__shortcuts');
    if (!shortcuts) return false;
    if (shortcuts.querySelector('[data-hallwines-account-logout]')) return true;

    var wrapper = document.createElement('div');
    var link = document.createElement('a');

    wrapper.className = 'account__item-wrapper';
    link.href = account.dataset.accountLogoutUrl;
    link.rel = 'nofollow';
    link.className = 'account__item';
    link.textContent = account.dataset.accountLogoutLabel || 'Log out';
    link.setAttribute('data-hallwines-account-logout', 'true');

    wrapper.appendChild(link);
    shortcuts.appendChild(wrapper);

    return true;
  }

  function observeAccountLogoutLink(account) {
    if (!account || !account.shadowRoot || account.__hallwinesLogoutObserver || typeof MutationObserver === 'undefined') return;

    account.__hallwinesLogoutObserver = new MutationObserver(function () {
      injectAccountLogoutLink(account);
    });
    account.__hallwinesLogoutObserver.observe(account.shadowRoot, { childList: true, subtree: true });
  }

  function scheduleAccountLogoutInjection(account) {
    var attempts = 0;

    function run() {
      attempts += 1;
      if (account && account.shadowRoot) observeAccountLogoutLink(account);
      if (injectAccountLogoutLink(account)) return;
      if (attempts < 6) {
        setTimeout(run, attempts * 100);
      }
    }

    run();
  }

  function installAccountLogoutLink() {
    var account = document.querySelector('shopify-account[data-account-logout-url]');
    if (!account) return;

    account.addEventListener('open', function () {
      scheduleAccountLogoutInjection(account);
    });

    if (window.customElements && typeof window.customElements.whenDefined === 'function') {
      window.customElements
        .whenDefined('shopify-account')
        .then(function () {
          scheduleAccountLogoutInjection(account);
        })
        .catch(function () {});
    } else {
      scheduleAccountLogoutInjection(account);
    }
  }

  function syncMemberSinceToCart() {
    var memberEl = document.querySelector('.account-button__member-since');
    if (!memberEl) return;
    var memberText = memberEl.textContent.trim();
    if (!memberText) return;

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var current = cart.attributes && cart.attributes.member_since;
        if (current === memberText) return;
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ attributes: { member_since: memberText } })
        });
      })
      .catch(function () {});
  }

  if (hostOk) handleInboundCartToken();
  reconcileLocalFromCart();

  function onReady() {
    if (hostOk) {
      patchCrossDomainLinks();
      observeForRepatching();
    }
    populateGreeting();
    installAccountLogoutLink();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
